// Shared Y2K/hypergrid CSS. One idempotent injection — safe to call
// from any module that renders a boot-time or in-match overlay. All
// rules are scoped under `body[data-theme="y2k"]` so other themes are
// untouched.
//
// Exposed class names:
//   y2k-card        — panel surface (chrome border, void fill, perspective grid)
//   y2k-heading     — big chrome-gradient title text
//   y2k-subtle      — small caps label, magenta
//   y2k-chip        — small pill tag with neon glow
//   y2k-btn         — primary CTA button (chrome + neon rim, hover lift)
//   y2k-btn-virgin   — variant with cyan accent
//   y2k-btn-inf     — variant with magenta accent
//   y2k-marquee     — scrolling strip container
//   y2k-star        — 8-point star decal (pseudo-elements)
//   y2k-scanlines   — inner CRT scanline overlay

const STYLE_ID = 'smp-y2k-css';

export function ensureY2kStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

export function isY2kActive(): boolean {
  if (typeof document === 'undefined') return false;
  return document.body.dataset.theme === 'y2k';
}

const CSS = `
@keyframes y2k-marquee {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
@keyframes y2k-pulse {
  0%,100% { text-shadow: 0 0 6px #ff1493, 0 0 14px #ff1493aa, 0 0 22px #00f0ff88; }
  50%     { text-shadow: 0 0 10px #00f0ff, 0 0 20px #00f0ffaa, 0 0 28px #ff1493aa; }
}
@keyframes y2k-drift {
  0%,100% { transform: translate(0,0) rotate(0deg); }
  50%     { transform: translate(2px,-3px) rotate(8deg); }
}
@keyframes y2k-sheen {
  0%   { background-position: -200% 50%; }
  100% { background-position:  200% 50%; }
}

body[data-theme="y2k"] .y2k-card {
  position: relative;
  background:
    linear-gradient(145deg, #140738 0%, #2a0a4a 60%, #5a0a5a 100%);
  border: 2px solid transparent;
  border-image: linear-gradient(135deg, #ff1493, #fff200, #00f0ff, #ff1493) 1;
  border-radius: 18px;
  box-shadow:
    0 0 0 1px #ffffff22 inset,
    0 0 24px #ff149355,
    0 24px 64px #00000099;
  color: #e8ddff;
  overflow: hidden;
}
body[data-theme="y2k"] .y2k-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    repeating-linear-gradient(180deg, transparent 0 2px, #ffffff08 2px 3px),
    repeating-linear-gradient(90deg,  transparent 0 12px, #ff149315 12px 13px),
    repeating-linear-gradient(180deg, transparent 0 12px, #00f0ff15 12px 13px);
  pointer-events: none;
  mix-blend-mode: screen;
}
body[data-theme="y2k"] .y2k-card::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: -60%;
  height: 120%;
  background:
    repeating-linear-gradient(180deg, transparent 0 8px, #ff149355 8px 9px),
    repeating-linear-gradient(90deg,  transparent 0 8px, #00f0ff55 8px 9px);
  transform: perspective(340px) rotateX(58deg);
  transform-origin: top center;
  opacity: .35;
  pointer-events: none;
}

body[data-theme="y2k"] .y2k-heading {
  font: 900 30px "Impact", "Arial Black", system-ui, sans-serif;
  letter-spacing: 4px;
  background: linear-gradient(90deg, #ffffff 0%, #ffb3e6 25%, #ffffff 50%, #aaffff 75%, #ffffff 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 0 6px #ff149380) drop-shadow(0 0 12px #00f0ff60);
  animation: y2k-sheen 3.5s linear infinite;
}

body[data-theme="y2k"] .y2k-subtle {
  font: 700 10px ui-monospace, monospace;
  letter-spacing: 3px;
  color: #ff66c4;
  text-shadow: 0 0 6px #ff149380;
  text-transform: uppercase;
}

body[data-theme="y2k"] .y2k-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff149333, #00f0ff33);
  border: 1px solid #ff149388;
  color: #fff;
  font: 800 10px ui-monospace, monospace;
  letter-spacing: 2px;
  text-shadow: 0 0 4px #00f0ff80;
}

body[data-theme="y2k"] .y2k-btn {
  position: relative;
  padding: 16px 20px;
  border: 2px solid transparent;
  border-radius: 14px;
  background:
    linear-gradient(#140738, #140738) padding-box,
    linear-gradient(135deg, #ff1493, #00f0ff) border-box;
  color: #fff;
  font: 900 14px ui-monospace, monospace;
  letter-spacing: 2px;
  cursor: pointer;
  box-shadow:
    inset 0 0 0 1px #ffffff22,
    0 0 0 0 #ff149300,
    0 6px 18px #00000088;
  transition: transform .12s ease, box-shadow .2s ease, filter .2s ease;
  overflow: hidden;
}
body[data-theme="y2k"] .y2k-btn::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, #ffffff22 45%, #ffffff44 50%, #ffffff22 55%, transparent 100%);
  transform: translateX(-100%);
  transition: transform .45s ease;
  pointer-events: none;
}
body[data-theme="y2k"] .y2k-btn:hover {
  transform: translateY(-3px) scale(1.03);
  filter: brightness(1.15);
  box-shadow:
    inset 0 0 0 1px #ffffff44,
    0 0 18px #ff1493aa,
    0 0 32px #00f0ff88,
    0 10px 24px #00000099;
}
body[data-theme="y2k"] .y2k-btn:hover::after { transform: translateX(100%); }
body[data-theme="y2k"] .y2k-btn-virgin {
  background:
    linear-gradient(#0a2340, #061e38) padding-box,
    linear-gradient(135deg, #00f0ff, #7c3aed) border-box;
  color: #aaffff;
  text-shadow: 0 0 6px #00f0ff80;
}
body[data-theme="y2k"] .y2k-btn-inf {
  background:
    linear-gradient(#3a0a28, #2a0820) padding-box,
    linear-gradient(135deg, #ff1493, #fff200) border-box;
  color: #ffcfe8;
  text-shadow: 0 0 6px #ff149380;
}

body[data-theme="y2k"] .y2k-marquee {
  position: relative;
  overflow: hidden;
  height: 20px;
  margin: 10px -16px -4px;
  border-top: 1px solid #ff149388;
  border-bottom: 1px solid #00f0ff88;
  background: linear-gradient(90deg, #140738, #2a0a4a, #140738);
}
body[data-theme="y2k"] .y2k-marquee-inner {
  display: inline-block;
  white-space: nowrap;
  padding-top: 4px;
  animation: y2k-marquee 18s linear infinite;
  font: 900 11px ui-monospace, monospace;
  letter-spacing: 4px;
  color: #fff200;
  text-shadow: 0 0 6px #ff1493aa;
}
body[data-theme="y2k"] .y2k-marquee-inner span {
  padding: 0 24px;
}
body[data-theme="y2k"] .y2k-marquee-inner .pink { color: #ffcfe8; }
body[data-theme="y2k"] .y2k-marquee-inner .cyan { color: #aaffff; }

body[data-theme="y2k"] .y2k-star {
  position: absolute;
  width: 22px; height: 22px;
  background: linear-gradient(135deg, #ffffff 0%, #fff200 50%, #ff1493 100%);
  clip-path: polygon(
    50% 0%, 58% 38%, 100% 50%, 58% 62%,
    50% 100%, 42% 62%, 0% 50%, 42% 38%
  );
  filter: drop-shadow(0 0 6px #fff200cc) drop-shadow(0 0 12px #ff1493aa);
  animation: y2k-drift 3.5s ease-in-out infinite;
  pointer-events: none;
}

body[data-theme="y2k"] .y2k-scanlines {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(180deg, transparent 0 2px, #ffffff08 2px 3px);
  pointer-events: none;
  z-index: 1;
}

/* ---- HUD overrides ---- */
body[data-theme="y2k"] #hud-hp {
  background: linear-gradient(90deg, #ff1493 0%, #fff200 50%, #00f0ff 100%) !important;
  box-shadow: 0 0 8px #ff149388, 0 0 2px #ffffff inset;
}
body[data-theme="y2k"] #hud-hp-label {
  color: #ffcfe8;
  text-shadow: 0 0 4px #ff149388;
}
body[data-theme="y2k"] #hud-xp {
  background: linear-gradient(90deg, #00f0ff 0%, #ffffff 50%, #fff200 100%) !important;
  box-shadow: 0 0 6px #00f0ff80;
}
body[data-theme="y2k"] #hud-xp-label {
  color: #aaffff;
  text-shadow: 0 0 4px #00f0ff88;
}
body[data-theme="y2k"] #hud-role {
  color: #fff;
  text-shadow: 0 0 6px #ff1493aa, 0 0 2px #00f0ffaa;
  letter-spacing: 2px !important;
}
body[data-theme="y2k"] #hud-level {
  color: #fff200 !important;
  opacity: 1 !important;
  text-shadow: 0 0 4px #ff149380;
}
body[data-theme="y2k"] #hud-score {
  background: linear-gradient(145deg, #140738dd, #2a0a4add) !important;
  border: 1px solid #ff149380;
  box-shadow: 0 0 12px #ff149355, 0 0 2px #ffffff inset;
  color: #fff;
}
body[data-theme="y2k"] #hud-score > span:first-child { color: #00f0ff !important; text-shadow: 0 0 8px #00f0ff; }
body[data-theme="y2k"] #hud-score > span:last-child  { color: #ff1493 !important; text-shadow: 0 0 8px #ff1493; }
body[data-theme="y2k"] #hud-phase {
  background: linear-gradient(145deg, #140738dd, #2a0a4add) !important;
  border: 1px solid #00f0ff88;
  color: #aaffff !important;
  text-shadow: 0 0 6px #00f0ff88;
  box-shadow: 0 0 8px #00f0ff55;
}
body[data-theme="y2k"] #hud-modesize {
  background: linear-gradient(145deg, #2a0a4add, #140738dd) !important;
  border: 1px solid #fff20066;
  color: #fff200 !important;
  text-shadow: 0 0 4px #ff149380;
  opacity: 1 !important;
}
body[data-theme="y2k"] #hud-combo-label {
  background: linear-gradient(145deg, #140738dd, #3a0a28dd) !important;
  border-color: #ff1493 !important;
  color: #fff !important;
  text-shadow: 0 0 8px #ff1493, 0 0 14px #ff149388 !important;
}
body[data-theme="y2k"] #hud-ult-bar {
  background: linear-gradient(90deg, #ff1493 0%, #fff200 50%, #00f0ff 100%) !important;
  box-shadow: 0 0 10px #ff149388;
}
body[data-theme="y2k"] #hud-ult-label {
  color: #fff200 !important;
  text-shadow: 0 0 6px #ff1493;
}
`;
